import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { DataTable } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { ExcelImportDialog } from "@/components/ExcelImportDialog";
import type { MasterItem, ColumnFilterOption } from "@/types";
import { formatGridDate } from "@/lib/utils";

const MASTER_LABELS: Record<string, string> = {
  authors: "Authors",
  categories: "Categories",
  publishers: "Publishers",
  classes: "Classes",
  sections: "Sections",
  racks: "Racks",
  shelves: "Shelves",
};

export default function MasterDataPage() {
  const { masterType } = useParams();
  const { api, user } = useAuth();
  const [items, setItems] = useState<MasterItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<MasterItem | null>(null);
  const [form, setForm] = useState<{ name: string; description: string; class_id: string; rack_id: string }>({
    name: "",
    description: "",
    class_id: "",
    rack_id: "",
  });
  const [parentItems, setParentItems] = useState<MasterItem[]>([]);
  const [importOpen, setImportOpen] = useState(false);
  const isAdmin = user?.role === "admin" || user?.role === "librarian";
  const label = MASTER_LABELS[masterType!] || masterType;
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
  const filterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const needsParent = masterType === "sections" || masterType === "shelves";
  const parentType = masterType === "sections" ? "classes" : masterType === "shelves" ? "racks" : "";
  const parentLabel = masterType === "sections" ? "Class" : masterType === "shelves" ? "Rack" : "";
  const parentKey = masterType === "sections" ? "class_id" : "rack_id";

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 50 };
      const textParts = [columnFilters.name, columnFilters.description].filter(Boolean);
      if (textParts.length) params.search = textParts.join(" ");
      if (columnFilters.parent_name) params.parent_id = columnFilters.parent_name;
      const res = await api.get(`/masters/${masterType}`, { params });
      setItems(res.data.items);
      setTotalPages(res.data.pages);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  }, [api, masterType, page, columnFilters]);

  useEffect(() => {
    setPage(1);
    setColumnFilters({});
  }, [masterType]);
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    if (needsParent && parentType) {
      api
        .get(`/masters/${parentType}/all`)
        .then((res) => setParentItems(res.data))
        .catch(() => {});
    } else {
      setParentItems([]);
    }
  }, [api, needsParent, parentType]);

  const handleColumnFiltersChange = useCallback((filters: Record<string, any>) => {
    setColumnFilters(filters);
    if (filterTimer.current) clearTimeout(filterTimer.current);
    filterTimer.current = setTimeout(() => setPage(1), 400);
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", description: "", class_id: "", rack_id: "" });
    setDialogOpen(true);
  };

  const openEdit = (item: MasterItem) => {
    setEditing(item);
    setForm({
      name: item.name,
      description: item.description || "",
      class_id: (item as any).class_id || "",
      rack_id: (item as any).rack_id || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) {
      toast.error("Name is required");
      return;
    }
    if (needsParent && !form[parentKey as keyof typeof form]) {
      toast.error(`${parentLabel} is required`);
      return;
    }
    const payload: any = { name: form.name, description: form.description };
    if (masterType === "sections") payload.class_id = form.class_id;
    if (masterType === "shelves") payload.rack_id = form.rack_id;
    try {
      if (editing) {
        await api.put(`/masters/${masterType}/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post(`/masters/${masterType}`, payload);
        toast.success("Created");
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/masters/${masterType}/${id}`);
      toast.success("Deleted");
      fetchItems();
    } catch {
      toast.error("Failed");
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/excel/export/${masterType}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${masterType}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const getParentName = (item: any) => {
    const pid = masterType === "sections" ? item.class_id : item.rack_id;
    const parent = parentItems.find((p) => p.id === pid);
    return parent?.name || "-";
  };

  const parentOptions: ColumnFilterOption[] = parentItems.map((p) => ({ label: p.name, value: p.id }));

  const columns: any[] = [
    {
      accessorKey: "name",
      header: "Name",
      meta: { filterVariant: "text", filterPlaceholder: "Name..." },
      cell: ({ row }: any) => <span className="font-medium">{row.original.name}</span>,
    },
    ...(needsParent
      ? [
          {
            id: "parent_name",
            header: parentLabel,
            meta: { filterVariant: "select", filterOptions: parentOptions, filterPlaceholder: parentLabel },
            cell: ({ row }: any) => <span>{getParentName(row.original)}</span>,
          },
        ]
      : []),
    { accessorKey: "description", header: "Description", meta: { filterVariant: "text", filterPlaceholder: "Description..." } },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }: any) => formatGridDate(row.original.created_at),
    },
    ...(isAdmin
      ? [
          {
            id: "actions",
            header: "Actions",
            cell: ({ row }: any) => (
              <div className="flex items-center gap-1" onClick={(e: any) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row.original)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(row.original.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-6" data-testid={`master-${masterType}-page`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {label}
          </h1>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid={`import-${masterType}-btn`}>
              <Upload className="w-4 h-4 mr-1.5" /> Import Excel
            </Button>
            <Button className="bg-primary hover:bg-primary/90" onClick={openNew} data-testid={`add-${masterType}-btn`}>
              <Plus className="w-4 h-4 mr-1.5" /> Add {label?.slice(0, -1)}
            </Button>
          </div>
        )}
      </div>

      <DataTable
        columns={columns}
        data={items}
        totalPages={totalPages}
        currentPage={page}
        onPageChange={setPage}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        loading={loading}
        onExport={handleExport}
        onRowClick={isAdmin ? openEdit : undefined}
        data-testid={`${masterType}-table`}
      />

      <ExcelImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        entityType={masterType!}
        entityLabel={label || masterType!}
        onSuccess={fetchItems}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit" : "Add"} {label?.slice(0, -1)}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`${masterType}-name-input`} />
            </div>
            {masterType === "sections" && (
              <div className="space-y-1.5">
                <Label>Class *</Label>
                <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select class" />
                  </SelectTrigger>
                  <SelectContent>
                    {parentItems.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {masterType === "shelves" && (
              <div className="space-y-1.5">
                <Label>Rack *</Label>
                <Select value={form.rack_id} onValueChange={(v) => setForm({ ...form, rack_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select rack" />
                  </SelectTrigger>
                  <SelectContent>
                    {parentItems.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="bg-primary hover:bg-primary/90" onClick={handleSave} data-testid={`save-${masterType}-btn`}>
              {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
